import { messages } from '@/shared/constants/messages';

/**
 * Tradução de erro técnico para frase acionável em português (RNF-U03).
 *
 * O contrato: a RPC levanta um erro cujo código é um slug em inglês
 * (`comment_required`), e o front-end é o único responsável por escolher a
 * frase em português. Banco não formata mensagem de usuário; UI não decide
 * regra de negócio (architecture.md §3).
 *
 * ⚠️ Os códigos abaixo são o contrato proposto. Quando as RPCs forem escritas,
 * eles precisam bater exatamente com o que as migrations levantam — um código
 * sem tradução cai no texto genérico e o voluntário fica sem saber o que fazer.
 */

export const RPC_ERROR_MESSAGES: Record<string, string> = {
  // Esteira (ADR-003)
  comment_required: 'Escreva um comentário explicando a decisão antes de continuar.',
  invalid_transition:
    'Esta ação não é válida para a situação atual do projeto. Atualize a página para ver a situação mais recente.',
  not_allowed_for_role: 'Seu papel não permite esta ação.',
  ministry_scope_violation: 'Este projeto pertence a outro ministério.',
  /** Escopo de overseer restrito à própria filial (ADR-008 item 6). */
  branch_scope_violation: 'Este projeto pertence a outra filial.',
  membership_expired:
    'Seu vínculo de liderança não está mais vigente. Fale com a secretaria da igreja.',
  /**
   * Esteira de decisão (spec-project-review.md §4.3, §4.9).
   *
   * ⚠️ O "Recarregue a página" SAIU (spec-project-review-ux.md decisão 26). O
   * RNF-U03 pede que toda falha diga o que fazer agora — e aqui não há nada
   * para o usuário fazer: `useDecideProject` invalida o detalhe e a linha do
   * tempo no próprio `onError`, o crachá muda, o painel some e o cabeçalho
   * passa a atribuir a decisão a quem chegou primeiro. Mandar recarregar era
   * pedir trabalho manual para o app mostrar a verdade que ele já tem.
   */
  already_decided: 'Este projeto já foi decidido por outra pessoa. A tela foi atualizada.',
  already_in_review: 'Outra pessoa já iniciou a análise deste projeto.',
  /** Guard de RequireAuth para as rotas autenticadas (US-04). */
  no_active_membership:
    'Sua conta não tem vínculo ativo com nenhum ministério. Fale com a secretaria da igreja.',

  // Rascunho (ADR-005)
  draft_version_conflict: messages.draft.conflict,

  // Formulário (US-15) — os slugs que `save_project_draft`, `submit_project` e
  // `discard_draft` levantam. Slug sem tradução cai no texto genérico e o
  // voluntário fica sem saber o que fazer; o teste de errors.test.ts existe
  // para que nenhum deles fique de fora no dia em que a RPC ganhar um novo.
  ends_before_starts: 'A data e o horário de término precisam ser depois do início.',
  /**
   * O rascunho envelheceu na gaveta: a data dele passou antes de alguém
   * apertar enviar. A frase manda remarcar, e não "corrigir" — o que aconteceu
   * não foi erro de digitação.
   */
  starts_in_past: 'A data de início já passou. Escolha uma data a partir de amanhã e envie de novo.',
  incomplete_project:
    'Ainda falta preencher algum campo obrigatório. Confira as seções marcadas no índice antes de enviar.',
  no_space_selected: 'Escolha pelo menos um espaço para o projeto.',
  space_inactive: 'Selecione um espaço da lista.',
  project_not_editable:
    'Este projeto está em análise e não pode ser editado agora. Se precisar mudar algo, fale com quem está analisando.',
  responsible_not_in_ministry:
    'A pessoa responsável precisa ter vínculo ativo neste ministério. Escolha alguém da lista.',
  catalog_item_inactive:
    'Uma das opções escolhidas não está mais disponível. Selecione outra da lista.',
  ministry_required: 'Escolha em nome de qual ministério o projeto será criado.',
  unknown_field:
    'O formulário enviou um campo que o sistema não reconhece. Recarregue a página e tente de novo.',
  not_found:
    'Não encontramos este projeto. Ele pode ter sido descartado ou você não tem acesso a ele.',
};

/** Códigos do PostgREST que têm tradução própria. */
const POSTGREST_CODES: Record<string, string> = {
  PGRST116: messages.errors.notFound,
  '42501': messages.errors.forbidden,
};

/**
 * Códigos de erro do GoTrue (`AuthApiError.code`) com tradução própria —
 * `otp_expired` cobre tanto código errado quanto código vencido, porque o
 * Supabase não diferencia os dois casos na resposta.
 *
 * `user_banned` usa DE PROPÓSITO a mesma frase de `otp_expired`, e não uma
 * mensagem própria de "conta encerrada": alguém que perdeu o acesso
 * (admin_revoke_all_access) precisa ver exatamente a mesma coisa que alguém
 * que só digitou o código errado — nunca uma pista de que a conta já existiu
 * e foi banida. Verificado em 2026-08-04 contra o GoTrue local: pedir código
 * (`/auth/v1/otp`) já responde 200 igual para banido e não-cadastrado (não
 * precisa de tratamento aqui); é só na verificação (`/auth/v1/verify`) que
 * o banido recebe `user_banned` em vez do `otp_expired` que um e-mail não
 * cadastrado recebe — esta linha fecha essa diferença.
 */
const AUTH_ERROR_CODES: Record<string, string> = {
  otp_expired: messages.auth.otpInvalid,
  user_banned: messages.auth.otpInvalid,
  over_email_send_rate_limit: messages.auth.otpRateLimited,
};

interface ErrorLike {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  status?: unknown;
}

function asErrorLike(error: unknown): ErrorLike {
  return typeof error === 'object' && error !== null ? (error as ErrorLike) : {};
}

/**
 * Extrai o slug da RPC. Erro levantado com `RAISE EXCEPTION USING
 * MESSAGE = 'comment_required'` chega em `message`; alguns caminhos o
 * devolvem em `details`.
 */
export function extractRpcCode(error: unknown): string | null {
  const { message, details } = asErrorLike(error);
  for (const candidate of [message, details]) {
    if (typeof candidate !== 'string') continue;
    const slug = candidate.trim();
    if (slug in RPC_ERROR_MESSAGES) return slug;
  }
  return null;
}

/**
 * Mensagem final para a interface. Nunca devolve texto do Postgres.
 * Use SEMPRE isto em toast e em bloco de erro de formulário.
 */
export function toUserMessage(error: unknown): string {
  if (import.meta.env.DEV) {
    // O detalhe técnico não pode chegar ao usuário, mas também não pode
    // sumir — sem ele, depurar relato de voluntário é adivinhação.
    console.error('[erro]', error);
  }

  const rpcCode = extractRpcCode(error);
  if (rpcCode) return RPC_ERROR_MESSAGES[rpcCode] ?? messages.errors.unexpected;

  const { code, status, message } = asErrorLike(error);
  if (typeof code === 'string' && code in AUTH_ERROR_CODES) {
    return AUTH_ERROR_CODES[code] ?? messages.errors.unexpected;
  }
  if (typeof code === 'string' && code in POSTGREST_CODES) {
    return POSTGREST_CODES[code] ?? messages.errors.unexpected;
  }
  if (status === 401 || status === 403) return messages.errors.forbidden;
  if (status === 404) return messages.errors.notFound;

  const isNetworkFailure =
    error instanceof TypeError ||
    (typeof message === 'string' && /fetch|network|failed to/i.test(message));
  if (isNetworkFailure) return messages.errors.network;

  return messages.errors.unexpected;
}

/** Verdadeiro quando o rascunho foi editado em outro dispositivo (ADR-005). */
export function isDraftVersionConflict(error: unknown): boolean {
  return extractRpcCode(error) === 'draft_version_conflict';
}
